import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useCurriculumVersionsListQuery } from '../../../types/planung-graphql-client-defs';

type CurriculumVersionsTableType = {
  uuid: string;
  name: string;
  description: string | null;
  used: boolean;
};

type CurriculumVersionsTableProps = {
  loading: boolean;
  onNavigate: (uuid: string) => void;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string, name: string) => void;
  readonly: boolean;
};

export const CurriculumVersionsTable = ({
  loading,
  onNavigate,
  onAdd,
  onEdit,
  onDelete,
  readonly,
}: CurriculumVersionsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('CURRICULUM_VERSIONS');

  const { sorting, saveSorting } = useColumnsSort('table-curriculum-versions');

  const [{ data: curriculumVersionsData }] = useCurriculumVersionsListQuery({
    context,
  });

  const columns: TableColumns<CurriculumVersionsTableType>[] = [
    {
      header: t('common.name'),
      id: 'name',
      accessorKey: 'name',
      size: 350,
    },
    {
      header: t('common.description'),
      id: 'description',
      accessorKey: 'description',
      canExpand: true,
    },
  ];

  const data: CurriculumVersionsTableType[] =
    curriculumVersionsData?.curriculumVersions.map((c) => ({
      uuid: c.uuid,
      name: c.name,
      description: c.description ?? null,
      used: c.curriculaCount > 0,
    })) ?? [];

  return (
    <Table<CurriculumVersionsTableType>
      showBorderRadius
      showShadow
      canScroll
      minHeight={1000}
      isOnWhite={false}
      onAddClick={onAdd}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('curriculumVersion.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('curriculumVersion.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
      }}
      showActionBar
      showSort={true}
      sorting={sorting}
      onSortingChange={saveSorting}
      actionBarSettings={{
        showExpertFilter: true,
        showAddButton: true,
        disableAddButton: readonly,
        showPrintButton: true,
      }}
      loading={loading}
      columns={columns}
      data={data}
      lastColWidth='165px'
      lastCol={({ original }) => {
        return (
          <ButtonGroup>
            <Button hierarchy='secondary' onClick={() => onNavigate(original.uuid ?? '')}>
              {t('curriculum.title', { count: 2 })}
            </Button>
            <Button
              hierarchy='secondary'
              onClick={() => {
                onEdit(original.uuid);
              }}
              icon={<EditIcon className='small' />}
              disabled={readonly}
            />
            <Button
              hierarchy='secondary'
              onClick={() => {
                onDelete(original.uuid, original.name);
              }}
              icon={<DeleteIcon color={readonly ? undefined : 'var(--color-error)'} className='small' />}
              disabled={readonly || original.used}
            />
          </ButtonGroup>
        );
      }}
    />
  );
};
